import { useEffect, useRef, useState } from 'react';

import { useProjectState, useStore } from './hooks.js';

/**
 * The license dialog (DESIGN_RECORD: the same email + key form that
 * sits on the product page, here on the tool's own locked surfaces).
 * No account, no password: the pair is the whole credential.
 */
export function LicenseDialog({ onClose }: { onClose: () => void }) {
  const store = useStore();
  const state = useProjectState();
  const [email, setEmail] = useState(state.license.email ?? '');
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const licensed = state.license.status === 'licensed';
  const dialogRef = useRef<HTMLDivElement>(null);

  // Modal keyboard contract: Escape closes, focus starts inside
  // the dialog, and Tab cycles within it while it is open.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog === null) return;
    const focusables = () =>
      [...dialog.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled])',
      )];
    focusables()[0]?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const items = focusables();
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const verify = async () => {
    setBusy(true);
    setMessage(null);
    const outcome = await store.verifyLicense(email, key);
    setBusy(false);
    if (outcome.ok) {
      setMessage('Unlocked. Every export and every area is now available.');
    } else {
      setMessage(outcome.message);
    }
  };

  return (
    <div className="dialog-backdrop" role="presentation" onClick={onClose}>
      <div
        ref={dialogRef}
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Unlock SAE Studio"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="dialog-brand">
          <span className="dotgrid" aria-hidden="true">
            <i></i>
            <i></i>
            <i></i>
            <i></i>
            <i></i>
            <i></i>
            <i></i>
            <i></i>
            <i></i>
          </span>
          <span className="wordmark">
            SAE <span className="wm-accent">Studio</span>
          </span>
        </div>
        <div className="unlock-eyebrow">Returning Buyer</div>
        <h2>Unlock with Your License</h2>
        <p>
          Enter the email address used at purchase and the license key
          from your receipt email. The pair unlocks SAE Studio on any
          machine. Verification sends your email address and the
          license key only; survey data never leaves the browser.
        </p>
        {licensed ? (
          <>
            <div className="unlock-success">
              <strong>Licensed.</strong> Unlocked with {state.license.email}
              {state.license.expiresAt !== null &&
                ` · license active through ${new Date(state.license.expiresAt).toLocaleDateString('en-US')}`}
              .
            </div>
            <div className="dialog-actions">
              <button
                className="btn"
                onClick={() => void store.signOutLicense()}
              >
                Remove This License from This Machine
              </button>
              <button className="btn btn-primary" onClick={onClose}>
                Done
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="fgroup">
              <label htmlFor="license-email">Email Used at Purchase</label>
              <input
                id="license-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.org"
              />
            </div>
            <div className="fgroup">
              <label htmlFor="license-key">License Key</label>
              <input
                id="license-key"
                type="text"
                autoComplete="off"
                value={key}
                onChange={(e) => setKey(e.target.value)}
                placeholder="Paste the key from your receipt email"
              />
            </div>
            {message !== null && (
              <p className="dialog-msg" role="status" aria-live="polite">{message}</p>
            )}
            {state.license.lastError !== null && message === null && (
              <p className="dialog-msg" role="status" aria-live="polite">{state.license.lastError}</p>
            )}
            <div className="dialog-actions">
              <button className="btn" onClick={onClose}>
                Cancel
              </button>
              <button
                className="btn btn-orange"
                disabled={busy || email.trim() === '' || key.trim() === ''}
                onClick={() => void verify()}
              >
                {busy ? 'Checking…' : 'Unlock the Tool'}
              </button>
            </div>
            <p className="unlock-help">
              Lost your key? A lost key can be re-sent only to the email
              address used at purchase, through the lost-key form on the
              product page. A license is $149 per year, purchased on the
              product page, and the key arrives in the receipt email.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
